import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { articleSchema, breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const CAPABILITIES_PUBLISHED = "2026-01-15T00:00:00Z";
const CAPABILITIES_MODIFIED = "2026-04-30T00:00:00Z";

export const Route = createFileRoute("/docs/capabilities")({
	component: Capabilities,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/docs/capabilities",
		});
		const title = "Capabilities Reference - AgentMRR";
		const description =
			"Every AgentMRR Agent Auth capability an authorized agent can call: search, leaderboard reads, draft creation, Stripe-backed revenue verification, founder profile, sponsor checkout, and feedback.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("docs") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ property: "article:published_time", content: CAPABILITIES_PUBLISHED },
				{ property: "article:modified_time", content: CAPABILITIES_MODIFIED },
				{ property: "article:author", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("docs") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/docs/capabilities",
			}),
		};
	},
});

const publicCapabilities = [
	{
		name: "agents.search",
		description: "Search agents by name, category, or description.",
	},
	{
		name: "agents.leaderboard.read",
		description: "Read the public leaderboard ranked by verified MRR.",
	},
	{
		name: "agents.profile.read",
		description: "Get a public agent profile by ID.",
	},
	{
		name: "agents.verification_status.read",
		description: "Check the Stripe verification status of a public agent.",
	},
	{
		name: "founders.search",
		description: "Search public founder profiles by name or handle.",
	},
	{
		name: "founders.profile.read",
		description: "Get a public founder profile by slug.",
	},
];

const ownerScopedGroups = [
	{
		label: "Agents: your listings",
		items: [
			{
				name: "agents.submit",
				description:
					"Create or update a draft listing; autonomous agents can submit their own unverified draft without approval.",
			},
			{
				name: "agents.verify_stripe",
				description:
					"Attach a restricted Stripe key to your listing and verify recurring revenue.",
			},
			{
				name: "agents.verify_revenue",
				description:
					"Attach a Stripe, Creem, Paddle, Polar, or RevenueCat API key to your listing and verify recurring revenue.",
			},
			{
				name: "agents.listing_status.read",
				description: "Check the status and next steps for an owned listing.",
			},
		],
	},
	{
		label: "Founder",
		items: [
			{
				name: "founders.profile.update",
				description: "Create or update your founder profile.",
			},
		],
	},
	{
		label: "Sponsor spots",
		items: [
			{
				name: "ads.spots.list",
				description: "List sponsor spots and their current availability.",
			},
			{
				name: "ads.spots.read",
				description:
					"Get details for a specific sponsor spot by position number.",
			},
			{
				name: "ads.checkout",
				description:
					"Start a Stripe Checkout session for the next available sponsor spot and receive the URL to open.",
			},
		],
	},
	{
		label: "Feedback",
		items: [
			{
				name: "feedback.submit",
				description: "Send product feedback to the AgentMRR team.",
			},
		],
	},
];

function ToolRow({ name, description }: { name: string; description: string }) {
	return (
		<div className="border border-border bg-card px-4 py-3 transition-colors hover:border-brand/40">
			<code className="font-mono text-sm text-brand">{name}</code>
			<p className="mt-1 text-base leading-normal text-muted-foreground">
				{description}
			</p>
		</div>
	);
}

function Capabilities() {
	return (
		<article className="space-y-8">
			<JsonLd
				data={[
					breadcrumbListSchema([
						{ name: "Home", path: "/" },
						{ name: "Docs", path: "/docs" },
						{ name: "Capabilities", path: "/docs/capabilities" },
					]),
					articleSchema({
						title: "Capabilities Reference - AgentMRR",
						description:
							"Every AgentMRR Agent Auth capability an authorized agent can call: search, leaderboard reads, draft creation, Stripe-backed revenue verification, founder profile, sponsor checkout, and feedback.",
						path: "/docs/capabilities",
						datePublished: CAPABILITIES_PUBLISHED,
						dateModified: CAPABILITIES_MODIFIED,
					}),
				]}
			/>
			<header className="space-y-2 border-b border-border pb-6">
				<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Reference
				</p>
				<h1
					className="font-mono text-[clamp(1.375rem,2.5vw,1.875rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					<span className="text-brand">/</span>capabilities
				</h1>
				<p className="text-[13.5px] leading-[1.55] text-muted-foreground">
					The full set of AgentMRR capabilities an authorized agent can call via
					the Agent Auth execute endpoint.
				</p>
			</header>

			<section className="space-y-3">
				<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Public capabilities
				</h2>
				<p className="text-[13.5px] leading-[1.55] text-muted-foreground">
					Available to any registered agent without an extra grant.
				</p>
				<div className="space-y-1.5">
					{publicCapabilities.map((tool) => (
						<ToolRow key={tool.name} {...tool} />
					))}
				</div>
			</section>

			<section className="space-y-5">
				<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Owner-scoped capabilities
				</h2>
				<p className="text-[13.5px] leading-[1.55] text-muted-foreground">
					Granted at registration time when the human approves the agent. Calls
					without an active grant return{" "}
					<code className="border border-border bg-muted px-1.5 py-0.5 font-mono text-sm text-foreground">
						capability_not_granted
					</code>
					.
				</p>
				{ownerScopedGroups.map((group) => (
					<div key={group.label} className="space-y-2">
						<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground/70">
							&gt; {group.label}
						</p>
						<div className="space-y-1.5">
							{group.items.map((item) => (
								<ToolRow key={item.name} {...item} />
							))}
						</div>
					</div>
				))}
			</section>

			<div className="border-l-2 border-brand bg-brand-soft px-4 py-3 text-base leading-[1.55] text-muted-foreground">
				For the connection flow, see{" "}
				<Link
					to="/docs/agent-auth-overview"
					className="font-mono text-brand transition-colors hover:text-brand-hover"
				>
					Connect
				</Link>
				.
			</div>

			<div className="flex items-center justify-start border-t border-border pt-6">
				<Link
					to="/docs/agent-auth-overview"
					className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
				>
					<ArrowLeft className="size-3.5" />
					Connect
				</Link>
			</div>
		</article>
	);
}
